import type { Feature } from 'geojson'
import { GeoJsonLayer, IconLayer, ScatterplotLayer } from '@deck.gl/layers'
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
} from '@vis.gl/react-google-maps'
import { useEffect, useMemo, useState } from 'react'
import { useMapCardSizes } from '~/hooks'
import { CLUSTER_ZOOM_LEVEL } from '~/utils'
import {
  type GoogleDeckDomEvent,
  GOOGLE_DECK_LAYER_ORDER,
  useGoogleDeckLayerGroup,
  useGoogleMapCameraTween,
} from '../google'
import {
  getListingCoordinates,
  getListingLatLng,
  getListingMarkerIconDefinition,
  getListingMarkerIconId,
  getSelectedListingMarkerIconId,
  isListingClusterFeature,
  normalizeListingsToPointFeatures,
  type ClusterOrListingPointFeature,
  type ListingClusterFeature,
  type ListingPointFeature,
  type MapListingFeatureInput,
} from './listingPointFeatures'
import { GoogleImageMarkers } from './GoogleImageMarkers'
import { GoogleListingHoverPopup } from './GoogleListingHoverPopup'
import { GoogleListingHoverPopover } from './GoogleListingHoverPopover'
import { useListingClusters } from './useListingClusters'
import { useDrawStyleTokens } from '../draw/drawStyleTokens'

export const GOOGLE_LISTING_PASSIVE_DRAWN_FEATURE_LAYER_ID =
  'google-map-listings-passive-drawn-feature'
export const GOOGLE_LISTING_HIT_LAYER_ID = 'google-map-listings-hit-area'
export const GOOGLE_LISTING_ICON_LAYER_ID = 'google-map-listings-icons'
export const GOOGLE_LISTING_CLUSTER_LAYER_ID = 'google-map-clusters'
export const GOOGLE_SELECTED_LISTING_LAYER_ID = 'google-map-selected-listing-icon'

const CLUSTER_CIRCLE_RADIUS_PX = 18
const CLUSTER_CIRCLE_FILL_RGBA: [number, number, number, number] = [
  124, 58, 237, 255,
]
const CLUSTER_CIRCLE_BORDER_RGBA: [number, number, number, number] = [
  124, 58, 237, 64,
]
const CLUSTER_CIRCLE_BORDER_WIDTH_PX = 4
const CLUSTER_LABEL_FONT_SIZE_PX = 12
const CLUSTER_LABEL_TEXT_COLOR = '#ffffff'
const CLUSTER_ZOOM_ANIMATION_DURATION_MS = 500
const LISTING_ICON_SIZE_PX = 20
const LISTING_SALE_ICON_SIZE_PX = 22
const LISTING_HIT_RADIUS_PX = 10
const SELECTED_LISTING_ICON_SIZE_PX = 26

export type GoogleListingLayersProps = {
  disableListingInteraction?: boolean
  useContainedHoverPopover?: boolean
  onBackgroundClick?: (
    latLng: google.maps.LatLngLiteral | null,
    domEvent: GoogleDeckDomEvent | null
  ) => void
  onListingClick?: (
    feature: ListingPointFeature,
    domEvent: GoogleDeckDomEvent | null
  ) => void
  onListingHoverChange?: (feature: ListingPointFeature | null) => void
  onPassiveDrawnFeatureClick?: (feature: Feature) => void
  passiveDrawnFeature?: Feature | null
  properties: MapListingFeatureInput[]
  selectedListingId?: string | null
  showImageMarkers?: boolean
}

export type GoogleZoomSafeClusterMarkersProps = {
  active?: boolean
  captureSnapshot?: boolean
  disableListingInteraction?: boolean
  properties: MapListingFeatureInput[]
  selectedListingId?: string | null
}

function getListingPointFeature(object: unknown) {
  const feature = object as ListingPointFeature | undefined

  if (!feature?.properties?._id) return null

  return feature
}

function getClusterRadius() {
  return CLUSTER_CIRCLE_RADIUS_PX
}

function getListingIconSize(feature: ListingPointFeature) {
  return feature.properties.iconType === 'sale'
    ? LISTING_SALE_ICON_SIZE_PX
    : LISTING_ICON_SIZE_PX
}

function hexToRgb(hex: `#${string}`): [number, number, number] {
  const normalized = hex.replace('#', '')
  const value = Number.parseInt(normalized, 16)

  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

function clusterContainsListing(
  cluster: ListingClusterFeature,
  selectedListingId: string,
  clusterIndex: ReturnType<typeof useListingClusters>['clusterIndex']
) {
  const clusterId = cluster.properties.cluster_id
  const pointCount = Number(cluster.properties.point_count ?? 0)

  if (!Number.isFinite(pointCount) || pointCount <= 0) return false

  return clusterIndex
    .getLeaves(clusterId, pointCount)
    .some((feature) => feature.properties._id === selectedListingId)
}

function GoogleClusterMarkers({
  clusterLabelFeatures,
  disableListingInteraction = false,
  onClusterClick,
  useZoomSafePresentation = false,
}: {
  clusterLabelFeatures: ListingClusterFeature[]
  disableListingInteraction?: boolean
  onClusterClick: (cluster: ListingClusterFeature) => void
  useZoomSafePresentation?: boolean
}) {
  return (
    <>
      {clusterLabelFeatures.map((cluster) => (
        <AdvancedMarker
          key={cluster.properties.cluster_id}
          position={getListingLatLng(cluster)}
          anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
          onClick={(event) => {
            event.domEvent?.stopPropagation()
            if (disableListingInteraction) return

            onClusterClick(cluster)
          }}>
          <div
            className="pointer-events-none flex select-none items-center justify-center font-bold leading-none text-white"
            style={{
              backgroundColor: useZoomSafePresentation
                ? '#7c3aed'
                : 'transparent',
              border: useZoomSafePresentation
                ? `${CLUSTER_CIRCLE_BORDER_WIDTH_PX}px solid rgba(124, 58, 237, 0.25)`
                : 'none',
              borderRadius: useZoomSafePresentation ? '9999px' : '0px',
              boxShadow: useZoomSafePresentation
                ? '0 4px 12px rgba(15, 23, 42, 0.18)'
                : 'none',
              width: `${getClusterRadius() * 2}px`,
              height: `${getClusterRadius() * 2}px`,
              fontSize: `${CLUSTER_LABEL_FONT_SIZE_PX}px`,
              color: CLUSTER_LABEL_TEXT_COLOR,
            }}>
            {cluster.properties.point_count_abbreviated}
          </div>
        </AdvancedMarker>
      ))}
    </>
  )
}

export function GoogleZoomSafeClusterMarkers({
  active = false,
  captureSnapshot = true,
  disableListingInteraction = false,
  properties,
  selectedListingId,
}: GoogleZoomSafeClusterMarkersProps) {
  const { animateCamera } = useGoogleMapCameraTween()
  const mapCardSizes = useMapCardSizes()
  const [frozenClusterLabelFeatures, setFrozenClusterLabelFeatures] = useState<
    ListingClusterFeature[]
  >([])
  const points = useMemo(
    () => normalizeListingsToPointFeatures(properties),
    [properties]
  )
  const { clusterFeatures, clusterIndex } = useListingClusters({
    points,
    selectedListingId,
  })

  const clusterLabelFeatures = useMemo(() => {
    if (!selectedListingId) return clusterFeatures

    return clusterFeatures.filter(
      (cluster) =>
        !clusterContainsListing(cluster, selectedListingId, clusterIndex)
    )
  }, [clusterFeatures, clusterIndex, selectedListingId])

  useEffect(() => {
    if (active || !captureSnapshot) return

    setFrozenClusterLabelFeatures(clusterLabelFeatures)
  }, [active, captureSnapshot, clusterLabelFeatures])

  const zoomToCluster = (cluster: ListingClusterFeature) => {
    const clusterId = cluster.properties.cluster_id
    const expansionZoom = clusterIndex.getClusterExpansionZoom(clusterId)
    const targetZoom = Math.max(expansionZoom + 1, CLUSTER_ZOOM_LEVEL + 1)

    animateCamera(
      {
        center: getListingLatLng(cluster),
        padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
        zoom: targetZoom,
      },
      { duration: CLUSTER_ZOOM_ANIMATION_DURATION_MS }
    )
  }

  return (
    <GoogleClusterMarkers
      clusterLabelFeatures={active ? frozenClusterLabelFeatures : []}
      disableListingInteraction={disableListingInteraction || active}
      onClusterClick={zoomToCluster}
      useZoomSafePresentation
    />
  )
}

export function GoogleListingLayers({
  disableListingInteraction = false,
  onBackgroundClick,
  onListingClick,
  onListingHoverChange,
  onPassiveDrawnFeatureClick,
  passiveDrawnFeature,
  properties,
  selectedListingId,
  showImageMarkers = false,
  useContainedHoverPopover = false,
}: GoogleListingLayersProps) {
  const drawStyles = useDrawStyleTokens()
  const [hoveredListingFeature, setHoveredListingFeature] =
    useState<ListingPointFeature | null>(null)
  const [hoveredListingPixel, setHoveredListingPixel] = useState<{
    x: number
    y: number
  } | null>(null)
  const { animateCamera } = useGoogleMapCameraTween()
  const mapCardSizes = useMapCardSizes()

  const points = useMemo(
    () => normalizeListingsToPointFeatures(properties),
    [properties]
  )

  const { clusterFeatures, clusterIndex, listingFeatures, selectedFeatures } =
    useListingClusters({
      points,
      selectedListingId,
    })

  const clusterLabelFeatures = useMemo(() => {
    if (!selectedListingId) return clusterFeatures

    return clusterFeatures.filter(
      (cluster) =>
        !clusterContainsListing(cluster, selectedListingId, clusterIndex)
    )
  }, [clusterFeatures, clusterIndex, selectedListingId])

  const layers = useMemo(
    () => [
      ...(passiveDrawnFeature
        ? [
            new GeoJsonLayer<Feature>({
              id: GOOGLE_LISTING_PASSIVE_DRAWN_FEATURE_LAYER_ID,
              data: passiveDrawnFeature,
              pickable: Boolean(onPassiveDrawnFeatureClick),
              stroked: true,
              filled: true,
              lineWidthUnits: 'pixels',
              lineWidthMinPixels: drawStyles.outlineWidth,
              getFillColor: [
                ...hexToRgb(drawStyles.fillColor),
                Math.round(drawStyles.fillOpacity * 255),
              ],
              getLineColor: [...hexToRgb(drawStyles.outlineColor), 255],
              getLineWidth: drawStyles.outlineWidth,
            }),
          ]
        : []),
      new ScatterplotLayer<ListingPointFeature>({
        id: GOOGLE_LISTING_HIT_LAYER_ID,
        data: listingFeatures,
        pickable: true,
        radiusUnits: 'pixels',
        stroked: false,
        filled: true,
        getFillColor: [0, 0, 0, 1],
        getPosition: getListingCoordinates,
        getRadius: LISTING_HIT_RADIUS_PX,
      }),
      new IconLayer<ListingPointFeature>({
        id: GOOGLE_LISTING_ICON_LAYER_ID,
        data: listingFeatures,
        pickable: false,
        alphaCutoff: 0.85,
        sizeUnits: 'pixels',
        sizeMinPixels: LISTING_ICON_SIZE_PX,
        sizeMaxPixels: LISTING_SALE_ICON_SIZE_PX,
        getPosition: getListingCoordinates,
        getSize: getListingIconSize,
        getIcon: (feature) =>
          getListingMarkerIconDefinition(
            getListingMarkerIconId(feature.properties)
          ),
      }),
      new ScatterplotLayer<ListingClusterFeature>({
        id: GOOGLE_LISTING_CLUSTER_LAYER_ID,
        data: clusterFeatures,
        pickable: true,
        stroked: true,
        filled: true,
        radiusUnits: 'pixels',
        lineWidthMinPixels: CLUSTER_CIRCLE_BORDER_WIDTH_PX,
        getFillColor: CLUSTER_CIRCLE_FILL_RGBA,
        getLineColor: CLUSTER_CIRCLE_BORDER_RGBA,
        getPosition: getListingCoordinates,
        getRadius: getClusterRadius,
      }),
      new IconLayer<ListingPointFeature>({
        id: GOOGLE_SELECTED_LISTING_LAYER_ID,
        data: selectedFeatures,
        pickable: false,
        alphaCutoff: 0.85,
        sizeUnits: 'pixels',
        sizeMinPixels: SELECTED_LISTING_ICON_SIZE_PX,
        sizeMaxPixels: SELECTED_LISTING_ICON_SIZE_PX,
        getPosition: getListingCoordinates,
        getSize: SELECTED_LISTING_ICON_SIZE_PX,
        getIcon: (feature) =>
          getListingMarkerIconDefinition(
            getSelectedListingMarkerIconId(feature.properties)
          ),
      }),
    ],
    [
      clusterFeatures,
      drawStyles.fillColor,
      drawStyles.fillOpacity,
      drawStyles.outlineColor,
      drawStyles.outlineWidth,
      listingFeatures,
      onPassiveDrawnFeatureClick,
      passiveDrawnFeature,
      selectedFeatures,
    ]
  )

  const zoomToCluster = (cluster: ListingClusterFeature) => {
    const clusterId = cluster.properties.cluster_id
    const expansionZoom = clusterIndex.getClusterExpansionZoom(clusterId)
    const targetZoom = Math.max(expansionZoom + 1, CLUSTER_ZOOM_LEVEL + 1)

    animateCamera(
      {
        center: getListingLatLng(cluster),
        padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
        zoom: targetZoom,
      },
      { duration: CLUSTER_ZOOM_ANIMATION_DURATION_MS }
    )
  }

  const clearListingHover = () => {
    setHoveredListingFeature(null)
    setHoveredListingPixel(null)
    onListingHoverChange?.(null)
  }

  const handleClick = (
    layerId: string | null,
    object: ClusterOrListingPointFeature | undefined,
    latLng: google.maps.LatLngLiteral | null,
    domEvent: GoogleDeckDomEvent | null
  ) => {
    if (
      disableListingInteraction &&
      (layerId === GOOGLE_LISTING_HIT_LAYER_ID ||
        layerId === GOOGLE_LISTING_CLUSTER_LAYER_ID ||
        layerId === GOOGLE_SELECTED_LISTING_LAYER_ID)
    ) {
      return
    }

    if (layerId === GOOGLE_LISTING_PASSIVE_DRAWN_FEATURE_LAYER_ID) {
      if (object) {
        onPassiveDrawnFeatureClick?.(object as unknown as Feature)
      }
      return
    }

    if (!object) {
      clearListingHover()
      onBackgroundClick?.(latLng, domEvent)
      return
    }

    if (isListingClusterFeature(object)) {
      clearListingHover()
      zoomToCluster(object)
      return
    }

    clearListingHover()
    onListingClick?.(object, domEvent)
  }

  useGoogleDeckLayerGroup({
    id: 'google-map-listing-layers',
    layers,
    order: GOOGLE_DECK_LAYER_ORDER.listings,
    clickPickRadius: LISTING_HIT_RADIUS_PX,
    hoverPickRadius: LISTING_HIT_RADIUS_PX,
    onBackgroundClick: ({ latLng, domEvent }) => {
      onBackgroundClick?.(latLng, domEvent)
    },
    onClick: ({ layerId, object, latLng, domEvent }) => {
      handleClick(
        layerId,
        object as ClusterOrListingPointFeature | undefined,
        latLng,
        domEvent
      )
    },
    onHover: (result) => {
      const hoveredFeature =
        !disableListingInteraction &&
        result?.layerId === GOOGLE_LISTING_HIT_LAYER_ID &&
        result.picked
          ? getListingPointFeature(result.object)
          : null

      setHoveredListingFeature(hoveredFeature)
      setHoveredListingPixel(hoveredFeature ? result?.pixel ?? null : null)
      onListingHoverChange?.(hoveredFeature)
    },
  })

  return (
    <>
      {hoveredListingFeature ? (
        useContainedHoverPopover && hoveredListingPixel ? (
          <GoogleListingHoverPopover
            listing={hoveredListingFeature.properties}
            pixel={hoveredListingPixel}
          />
        ) : (
          <GoogleListingHoverPopup listing={hoveredListingFeature.properties} />
        )
      ) : null}
      {showImageMarkers ? (
        <GoogleImageMarkers
          features={listingFeatures}
          onListingClick={disableListingInteraction ? undefined : onListingClick}
        />
      ) : null}
      <GoogleClusterMarkers
        clusterLabelFeatures={clusterLabelFeatures}
        disableListingInteraction={disableListingInteraction}
        onClusterClick={zoomToCluster}
      />
    </>
  )
}
